import {
  login,
  postLiveEvent,
  putMatchDoc,
  type AuthToken,
  type ApiResponse,
  type CouchPutResult,
} from "./api";
import {
  loadState,
  saveState,
  currentPhase,
  activeKickOff,
  type AppState,
} from "./state";
import { newMatchId, buildDefaultMatchPhone } from "./match";
import {
  buildKickOffEvent,
  buildHalfEndEvent,
  buildHalfTimeEvent,
  buildSecondHalfKickOffEvent,
  buildSecondHalfEndEvent,
  buildGoalEvent,
  buildIncidentEvent,
  buildSubstitutionEvent,
  type Side,
  type HalfNumber,
} from "./events";
import {
  buildTimeWindow,
  viewerUrl,
  matchByIdUrl,
  nonsseUrl,
  sseUrl,
} from "./endpoints";
import {
  planRandomMatch,
  randomStarterNumber,
  randomCardReason,
  RANDOM_MATCH_MISCONDUCT_CODE_ID,
} from "./randomMatch";

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
};

const state: AppState = loadState();
let timerHandle: number | null = null;

// Hardcoded for now — flip back to a selector here if we need to test other envs.
const BACKEND_URL = "https://auth-dev.refsix.com";

// ---------- rendering ----------

function render(): void {
  if (state.auth) {
    $("auth-form").classList.add("hidden");
    $("auth-status").classList.remove("hidden");
    $("auth-user").textContent = state.auth.user_id;
  } else {
    $("auth-form").classList.remove("hidden");
    $("auth-status").classList.add("hidden");
  }

  const m = state.match;
  const phase = currentPhase(m);
  $("match-id").textContent = m?.matchId ?? "—";

  const status = !m
    ? "no match"
    : !m.created
    ? "creating…"
    : phase === "ended"
    ? "ended"
    : phase === 1
    ? "1st half in progress"
    : phase === 2
    ? "2nd half in progress"
    : "created (not started)";
  $("match-status").textContent = status;

  const loggedIn = !!state.auth;
  const matchReady = !!m?.created;
  const phaseIsPlaying = phase === 1 || phase === 2;

  ($("new-match-btn") as HTMLButtonElement).disabled = !loggedIn;
  ($("random-match-btn") as HTMLButtonElement).disabled = !loggedIn;
  ($("start-match-btn") as HTMLButtonElement).disabled =
    !loggedIn || !matchReady || phase !== "none";
  ($("start-second-half-btn") as HTMLButtonElement).disabled =
    !loggedIn || phase !== 1;
  ($("end-match-btn") as HTMLButtonElement).disabled =
    !loggedIn || !phaseIsPlaying;

  document.querySelectorAll<HTMLButtonElement>(".event-buttons button").forEach((btn) => {
    btn.disabled = !loggedIn || !phaseIsPlaying;
  });

  renderEndpoints();
  renderTimer();
}

function renderEndpoints(): void {
  const m = state.match;
  const urls: Record<string, string | null> = m
    ? (() => {
        const win = buildTimeWindow({
          kickOffTime: m.firstHalfKickOff,
          endTime: m.endTime,
        });
        return {
          viewer: viewerUrl(m.matchId),
          match: matchByIdUrl(m.matchId),
          nonsse: nonsseUrl(win),
          sse: sseUrl(win),
        };
      })()
    : { viewer: null, match: null, nonsse: null, sse: null };

  for (const key of ["viewer", "match", "nonsse", "sse"] as const) {
    const li = document.querySelector<HTMLLIElement>(`li[data-endpoint="${key}"]`);
    if (!li) continue;
    const anchor = li.querySelector<HTMLAnchorElement>("a.link-btn");
    const code = li.querySelector<HTMLElement>(".endpoint-url");
    const url = urls[key];
    if (url && anchor && code) {
      anchor.href = url;
      anchor.classList.remove("disabled");
      anchor.removeAttribute("aria-disabled");
      code.textContent = url;
    } else if (anchor && code) {
      anchor.removeAttribute("href");
      anchor.classList.add("disabled");
      anchor.setAttribute("aria-disabled", "true");
      code.textContent = "—";
    }
  }
}

function renderTimer(): void {
  const m = state.match;
  const timerEl = $("timer");
  const badgeEl = $("half-badge");
  const phase = currentPhase(m);
  const kickOff = activeKickOff(m);

  if (!m || !kickOff) {
    timerEl.textContent = "00:00";
    badgeEl.textContent = "";
    return;
  }

  badgeEl.textContent =
    phase === 1 ? "Half 1" : phase === 2 ? "Half 2" : phase === "ended" ? "Full Time" : "";

  const end = phase === "ended" ? m.endTime ?? Date.now() : Date.now();
  const elapsedMs = Math.max(0, end - kickOff);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  timerEl.textContent = `${mm}:${ss}`;
}

function startTimerLoop(): void {
  if (timerHandle !== null) return;
  timerHandle = window.setInterval(renderTimer, 1000);
}
function stopTimerLoop(): void {
  if (timerHandle !== null) {
    clearInterval(timerHandle);
    timerHandle = null;
  }
}

// ---------- logging ----------

function logEntry(opts: {
  kind: "success" | "error" | "info";
  title: string;
  request?: unknown;
  response?: { status: number; body: unknown } | null;
}): void {
  const wrap = document.createElement("div");
  wrap.className = `log-entry ${opts.kind}`;
  const time = new Date().toLocaleTimeString();

  const header = document.createElement("div");
  header.innerHTML = `<span class="log-time">${time}</span><span class="log-status">${opts.title}</span>`;
  wrap.appendChild(header);

  if (opts.request !== undefined) {
    const pre = document.createElement("pre");
    pre.textContent = `→ ${JSON.stringify(opts.request, null, 2)}`;
    wrap.appendChild(pre);
  }
  if (opts.response) {
    const pre = document.createElement("pre");
    const bodyStr =
      typeof opts.response.body === "string"
        ? opts.response.body
        : JSON.stringify(opts.response.body, null, 2);
    pre.textContent = `← ${opts.response.status} ${bodyStr}`;
    wrap.appendChild(pre);
  }
  $("log").prepend(wrap);
}

// ---------- handlers ----------

function getSide(): Side {
  const checked = document.querySelector<HTMLInputElement>(
    'input[name="team-side"]:checked'
  );
  return (checked?.value as Side) ?? "home";
}

async function handleLogin(): Promise<void> {
  const username = ($("username") as HTMLInputElement).value.trim();
  const password = ($("password") as HTMLInputElement).value;
  if (!username || !password) {
    logEntry({ kind: "error", title: "Login skipped — missing username or password" });
    return;
  }
  logEntry({ kind: "info", title: `POST /auth/login (${username})` });
  try {
    const res = await login(BACKEND_URL, username, password);
    if (res.ok && typeof res.body === "object" && res.body !== null) {
      state.auth = res.body as AuthToken;
      saveState(state);
      const dbInfo = state.auth.userDBs?.supertest ? "(token + userDB stored)" : "(token stored; no userDBs returned!)";
      logEntry({
        kind: "success",
        title: `Login OK as ${state.auth.user_id}`,
        response: { status: res.status, body: dbInfo },
      });
      ($("password") as HTMLInputElement).value = "";
    } else {
      logEntry({
        kind: "error",
        title: "Login failed",
        response: { status: res.status, body: res.body },
      });
    }
  } catch (err) {
    logEntry({ kind: "error", title: `Login network error: ${(err as Error).message}` });
  }
  render();
}

function handleLogout(): void {
  state.auth = null;
  saveState(state);
  logEntry({ kind: "info", title: "Logged out (token cleared)" });
  render();
}

async function handleNewMatch(): Promise<void> {
  if (!state.auth) return;
  const userDbUrl = state.auth.userDBs?.supertest;
  if (!userDbUrl) {
    logEntry({
      kind: "error",
      title: "Cannot create match — login response had no userDBs.supertest URL",
    });
    return;
  }

  stopTimerLoop();
  const matchId = newMatchId();
  const externalMatchId = ($("external-match-id") as HTMLInputElement).value;
  const doc = buildDefaultMatchPhone(
    matchId,
    new Date().toISOString(),
    externalMatchId
  );

  state.match = {
    matchId,
    rev: null,
    created: false,
    firstHalfKickOff: null,
    firstHalfEnd: null,
    secondHalfKickOff: null,
    endTime: null,
  };
  saveState(state);
  render();

  logEntry({ kind: "info", title: `PUT ${matchId} on user database`, request: doc });

  try {
    const res = await putMatchDoc(userDbUrl, state.auth, matchId, doc);
    if (res.ok && typeof res.body === "object" && res.body !== null) {
      const result = res.body as CouchPutResult;
      state.match.rev = result.rev;
      state.match.created = true;
      saveState(state);
      logEntry({
        kind: "success",
        title: `Match created — rev ${result.rev}`,
        response: { status: res.status, body: result },
      });
    } else {
      logEntry({
        kind: "error",
        title: `Match create failed (${res.status})`,
        response: { status: res.status, body: res.body },
      });
    }
  } catch (err) {
    logEntry({
      kind: "error",
      title: `Match create network error: ${(err as Error).message}`,
    });
  }

  render();
}

async function postEvent(
  title: string,
  event: Record<string, unknown>
): Promise<ApiResponse<string> | null> {
  if (!state.auth || !state.match) return null;
  try {
    const res = await postLiveEvent(
      BACKEND_URL,
      state.auth,
      state.match.matchId,
      event
    );
    logEntry({
      kind: res.ok ? "success" : "error",
      title: `${title} — ${res.status}`,
      request: event,
      response: { status: res.status, body: res.body },
    });
    return res;
  } catch (err) {
    logEntry({
      kind: "error",
      title: `${title} — network error: ${(err as Error).message}`,
      request: event,
    });
    return null;
  }
}

// Bulk-creates a fully historical match: PUT the match doc with a 2-hour-ago
// kickoff and then POST every event (kickoff, randomised goals/cards/subs,
// half-time, second-half kickoff, full time) in chronological order. No live
// timers — the resulting match is already "ended" when this returns.
async function handleRandomMatch(): Promise<void> {
  if (!state.auth) return;
  const userDbUrl = state.auth.userDBs?.supertest;
  if (!userDbUrl) {
    logEntry({
      kind: "error",
      title: "Cannot create random match — login response had no userDBs.supertest URL",
    });
    return;
  }

  const newBtn = $("new-match-btn") as HTMLButtonElement;
  const randomBtn = $("random-match-btn") as HTMLButtonElement;
  newBtn.disabled = true;
  randomBtn.disabled = true;

  stopTimerLoop();

  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const firstHalfKickOff = Date.now() - TWO_HOURS_MS;
  const plan = planRandomMatch(firstHalfKickOff);

  const matchId = newMatchId();
  const externalMatchId = ($("external-match-id") as HTMLInputElement).value;
  const doc = buildDefaultMatchPhone(
    matchId,
    new Date(plan.timings.firstHalfKickOff).toISOString(),
    externalMatchId,
    RANDOM_MATCH_MISCONDUCT_CODE_ID
  );

  // Seed state.match with every boundary timestamp so the endpoints panel
  // gets a well-formed time window and currentPhase() returns "ended".
  state.match = {
    matchId,
    rev: null,
    created: false,
    firstHalfKickOff: plan.timings.firstHalfKickOff,
    firstHalfEnd: plan.timings.firstHalfEnd,
    secondHalfKickOff: plan.timings.secondHalfKickOff,
    endTime: plan.timings.secondHalfEnd,
  };
  saveState(state);
  render();

  logEntry({
    kind: "info",
    title: `PUT ${matchId} on user database (random match, ${plan.events.length} events)`,
    request: doc,
  });

  try {
    const res = await putMatchDoc(userDbUrl, state.auth, matchId, doc);
    if (res.ok && typeof res.body === "object" && res.body !== null) {
      const result = res.body as CouchPutResult;
      state.match.rev = result.rev;
      state.match.created = true;
      saveState(state);
      logEntry({
        kind: "success",
        title: `Random match created — rev ${result.rev}`,
        response: { status: res.status, body: result },
      });
    } else {
      logEntry({
        kind: "error",
        title: `Random match create failed (${res.status})`,
        response: { status: res.status, body: res.body },
      });
      render();
      return;
    }
  } catch (err) {
    logEntry({
      kind: "error",
      title: `Random match create network error: ${(err as Error).message}`,
    });
    render();
    return;
  }

  for (const ev of plan.events) {
    await postEvent(ev.title, ev.payload);
  }

  render();
}

async function handleStartMatch(): Promise<void> {
  if (!state.match?.created) return;
  const kickOffTime = Date.now();
  state.match.firstHalfKickOff = kickOffTime;
  state.match.firstHalfEnd = null;
  state.match.secondHalfKickOff = null;
  state.match.endTime = null;
  saveState(state);
  render();
  startTimerLoop();
  await postEvent("1st-half kickoff (Half)", buildKickOffEvent(kickOffTime));
}

// Single button that performs the full transition the watch performs across
// half time: end 1st half, post a (skipped) Half Time interval, start 2nd half.
//
// The four moments (1st-half end, HT start, HT end, 2nd-half kickoff) must be
// strictly increasing timestamps — same-millisecond collisions confuse
// consumers that sort events by `endTime` or `timestamp`. We space them by
// 1 ms so the transition is still visually instant but discrete.
async function handleStartSecondHalf(): Promise<void> {
  const m = state.match;
  if (!m || !m.firstHalfKickOff || m.secondHalfKickOff || m.endTime) return;

  const firstHalfEnd = Date.now();
  const halfTimeStart = firstHalfEnd + 1;
  const halfTimeEnd = firstHalfEnd + 2;
  const secondHalfKickOff = firstHalfEnd + 3;

  m.firstHalfEnd = firstHalfEnd;
  m.secondHalfKickOff = secondHalfKickOff;
  saveState(state);
  render();
  startTimerLoop();

  await postEvent(
    "1st-half end (Half re-post w/ endTime)",
    buildHalfEndEvent(m.firstHalfKickOff, firstHalfEnd)
  );
  await postEvent(
    "Half Time (start+end)",
    buildHalfTimeEvent(halfTimeStart, halfTimeEnd)
  );
  await postEvent(
    "2nd-half kickoff (Half)",
    buildSecondHalfKickOffEvent(secondHalfKickOff)
  );
}

async function handleEndMatch(): Promise<void> {
  const m = state.match;
  if (!m) return;
  const phase = currentPhase(m);
  if (phase !== 1 && phase !== 2) return;

  const endTime = Date.now();
  m.endTime = endTime;
  saveState(state);
  stopTimerLoop();
  render();

  if (phase === 1 && m.firstHalfKickOff) {
    m.firstHalfEnd = endTime;
    saveState(state);
    await postEvent(
      "Full Time — 1st-half ended early",
      buildHalfEndEvent(m.firstHalfKickOff, endTime)
    );
  } else if (phase === 2 && m.secondHalfKickOff) {
    await postEvent(
      "Full Time — 2nd-half ended",
      buildSecondHalfEndEvent(m.secondHalfKickOff, endTime)
    );
  }
}

async function handleEventButton(kind: string): Promise<void> {
  const m = state.match;
  if (!m) return;
  const phase = currentPhase(m);
  if (phase !== 1 && phase !== 2) return;
  const kickOff = activeKickOff(m);
  if (!kickOff) return;

  const half = phase as HalfNumber;
  const now = Date.now();
  const side = getSide();
  switch (kind) {
    case "goal":
      await postEvent(
        `Goal (${side}, half ${half})`,
        buildGoalEvent(kickOff, side, now, half)
      );
      break;
    case "yellow":
      await postEvent(
        `Yellow card (${side}, half ${half})`,
        buildIncidentEvent(
          kickOff,
          side,
          now,
          "yellow",
          half,
          randomStarterNumber(),
          randomCardReason("yellow")
        )
      );
      break;
    case "red":
      await postEvent(
        `Red card (${side}, half ${half})`,
        buildIncidentEvent(
          kickOff,
          side,
          now,
          "red",
          half,
          randomStarterNumber(),
          randomCardReason("red")
        )
      );
      break;
    case "substitution":
      await postEvent(
        `Substitution (${side}, half ${half})`,
        buildSubstitutionEvent(kickOff, side, now, half)
      );
      break;
  }
}

// ---------- wire-up ----------

function wire(): void {
  $("login-btn").addEventListener("click", handleLogin);
  $("logout-btn").addEventListener("click", handleLogout);
  $("new-match-btn").addEventListener("click", () => void handleNewMatch());
  $("random-match-btn").addEventListener("click", () => void handleRandomMatch());
  $("start-match-btn").addEventListener("click", () => void handleStartMatch());
  $("start-second-half-btn").addEventListener("click", () => void handleStartSecondHalf());
  $("end-match-btn").addEventListener("click", () => void handleEndMatch());

  document.querySelectorAll<HTMLButtonElement>(".event-buttons button").forEach((btn) => {
    btn.addEventListener("click", () => {
      const kind = btn.getAttribute("data-event");
      if (kind) void handleEventButton(kind);
    });
  });

  $("clear-log-btn").addEventListener("click", () => {
    $("log").innerHTML = "";
  });

  ($("password") as HTMLInputElement).addEventListener("keydown", (e) => {
    if (e.key === "Enter") void handleLogin();
  });
}

wire();
// If the page reloads mid-match, resume the timer in whichever half is active.
const resumePhase = currentPhase(state.match);
if (resumePhase === 1 || resumePhase === 2) {
  startTimerLoop();
}
render();
